/* eslint-disable @typescript-eslint/no-explicit-any */
// 2. Third-party library imports
import { get } from 'lodash-es';
import {
    call,
    all,
    delay,
    fork,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';

// 3. Local imports Constants
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay
} from '../../config/config';

// 4. Local imports Utility functions
import {
    checkShouldRequestRefetch,
    getTranslatedText
} from '../../utils/commonFunctions';
import { getTemplateContentType, isHtml } from '../../utils/contentFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

// 5. Local imports GraphQL
import { _queries as queries } from '../../graphql/queries.graphql';

// 6. Local imports API client
import client, { post } from 'src/api';

// 7. Local imports Redux actions & types
import { getPaymentPlanEnabled } from '../companies/companySlice';
import {
    getContentTemplateAction,
    getContentTemplateSuccess,
    getContentTemplateError,
    getContentTemplatePreviewAction,
    getContentTemplatePreviewSuccess,
    getContentTemplatePreviewError,
    getContentTemplateConfigurationAction,
    getContentTemplateConfigurationSuccess,
    getContentTemplateConfigurationError,
    getManualCommunicationTemplatesAction,
    getManualCommunicationTemplatesActionSuccess,
    getManualCommunicationTemplatesActionError,
    getWorkflowContentConfigurationAction,
    getWorkflowContentConfigurationSuccess,
    getWorkflowContentConfigurationError,
    saveContentTemplateAction,
    saveContentTemplateSuccess,
    saveContentTemplateError,
    getSelectedContentTemplate,
    uploadImageForEditorAction
} from './contentSlice';
import {
    ContentsState,
    ContentTemplateConfiguration,
    WorkflowContentConfiguration,
    GetContentTemplatePayload,
    GetContentTemplatePreviewPayload,
    GetManualCommunicationTemplatesRequestPayload
} from './types';

/**
 * Handlers
 */
function* handleGetWorkflowContentConfiguration() {
    let refetchCount = 0;
    const errorMessage = 'Error fetching content list. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_WORKFLOW_CONTENT_CONFIGURATION
            }
        );

        const WorkflowContentConfigurations: WorkflowContentConfiguration[] = get(res.data, 'GetWorkflowContentConfigurationForCompany');

        if (WorkflowContentConfigurations) {
            refetchCount = 0;

            yield put(getWorkflowContentConfigurationSuccess(WorkflowContentConfigurations));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getWorkflowContentConfigurationAction());
        }
        else {
            yield put(getWorkflowContentConfigurationError([errorMessage]));
        }
    }
}

function* handleGetContentTemplateConfiguration() {
    let refetchCount = 0;
    const errorMessage = 'Error fetching content list. Please try again later.';
    const PaymentPlanEnabled: boolean = yield select(getPaymentPlanEnabled);

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONTENT_TEMPLATE_CONFIGURATION,
                variables: {
                    PaymentPlanEnabled
                }
            }
        );

        const ContentTemplateConfiguration: ContentTemplateConfiguration = get(res.data, 'GetContentTemplateConfigurationForCompany');

        if (ContentTemplateConfiguration) {
            refetchCount = 0;

            yield put(getContentTemplateConfigurationSuccess(ContentTemplateConfiguration));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getContentTemplateConfigurationAction());
        }
        else {
            yield put(getContentTemplateConfigurationError([errorMessage]));
        }
    }
}

function* handleGetContentTemplate(sagaPayload: any) {
    const { payload }: { payload: GetContentTemplatePayload } = sagaPayload;
    const TemplateKey = get(payload, 'TemplateKey');
    const ContentType = getTemplateContentType(TemplateKey);
    let refetchCount = 0;
    const errorMessage = 'Error fetching content template. Please try again later.';

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONTENT_TEMPLATE, 
                variables: {
                    ...payload,
                    ContentType
                }
            }
        );

        const ContentTemplate = get(res.data, 'GetContentTemplateForCompany');

        refetchCount = 0;

        if (ContentTemplate) {
            yield put(getContentTemplateSuccess({
                ...ContentTemplate,
                IsHtml: isHtml(TemplateKey),
                ContentType
            }));
        }
        else {
            // ContentTemplate is null - this is a valid response when no template exists yet
            yield put(getContentTemplateSuccess({
                Content: '',
                Payload: '',
                Preview: '',
                IsHtml: isHtml(TemplateKey),
                ContentType
            }));
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getContentTemplateAction(payload));
        }
        else {
            yield put(getContentTemplateError([errorMessage]));
        }
    }
}

function* handleGetContentTemplatePreview({ payload: sagaPayload }: any) {
    const { payload, version }: { payload: GetContentTemplatePreviewPayload, version: string } = sagaPayload;
    const selectedContentTemplate: ContentsState['activeData']['record'] = yield select(getSelectedContentTemplate);
    let refetchCount = 0;
    const errorMessage = `Error rendering the preview, please check to make sure there aren't any invalid changes in the template`;

    try {
        // To call async functions, use redux-saga's `call()`.
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONTENT_TEMPLATE_PREVIEW, 
                variables: {
                    ...payload,
                    ContentType: get(selectedContentTemplate, 'ContentType')
                }
            }
        );

        const Preview: string | undefined = get(res.data, 'GetContentTemplatePreview.Preview');

        if (Preview) {
            refetchCount = 0;

            yield put(getContentTemplatePreviewSuccess({
                content: Preview,
                version
            }));
        }
        else {
            throw new Error('Preview not found!');
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getContentTemplatePreviewAction({
                payload,
                version
            }));
        }
        else {
            yield put(getContentTemplatePreviewError({
                previewErrors: [errorMessage],
                version
            }));
        }
    }
}

function* handleSaveContentTemplate({ payload: sagaPayload }: any) {
    const { callback, payload } = sagaPayload;

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/content/save/content-template',
            options: {
                body: payload
            }
        });
        
        const { NewTemplateKey } = yield body.json();
        if (callback) {
            const response = {
                ...payload,
                NewTemplateKey
            };

            yield put(saveContentTemplateSuccess(response));

            callback({
                ...response,
                IsSuccess: true
            });
        }
    }
    catch (err) {
        const error: any = err;
        yield put(saveContentTemplateError());

        if (callback) {
            let returnData: DynamicObject = { Messages: ['An error occurred'] };

            // Helper function to extract error data from any source
            const extractErrorData = (source: any): DynamicObject | null => {
                if (!source) return null;
                
                if (typeof source === 'string') {
                    // Source is JSON string, try to parse it
                    try {
                        const parsed = JSON.parse(source);
                        if (parsed && typeof parsed === 'object') {
                            return parsed;
                        }
                    } catch (parseError) {
                        // If parse fails, treat as plain text message
                        return { Messages: [source] };
                    }
                } else if (typeof source === 'object') {
                    // Source is already an object
                    return source;
                }
                
                return null;
            };

            // Try multiple locations for error data (supports all response formats)
            let extracted = false;
            
            // 1. Try error.response.body (new Amplify v6 format)
            if (error.response?.body && !extracted) {
                const data = extractErrorData(error.response.body);
                if (data) {
                    returnData = data;
                    extracted = true;
                }
            }
            
            // 2. Try error.response.data (old format for backward compatibility)
            if (!extracted && get(error.response, 'data')) {
                const data = extractErrorData(error.response.data);
                if (data) {
                    returnData = data;
                    extracted = true;
                }
            }
            
            // 3. Try error.response itself
            if (!extracted && error.response && typeof error.response === 'object') {
                // Check if response has Messages or NewTemplateKey directly
                if (error.response.Messages || error.response.NewTemplateKey !== undefined) {
                    returnData = error.response;
                    extracted = true;
                }
            }
            
            // 4. Try error object directly
            if (!extracted && (error.Messages || error.NewTemplateKey !== undefined)) {
                returnData = error;
                extracted = true;
            }
            
            // 5. Last resort: use error.message
            if (!extracted && error.message) {
                returnData = { Messages: [error.message] };
            }

            // Ensure returnData always has a valid Messages array
            if (!returnData.Messages || !Array.isArray(returnData.Messages) || returnData.Messages.length === 0) {
                returnData.Messages = [error.message || 'An error occurred'];
            }

            returnData.IsSuccess = false;

            callback(returnData);
        }

        if (error instanceof Error) {
            console.log('Error', error);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

function* handleGetManualCommunicationTemplateOptions({ payload: sagaPayload }: any) {
    const { callback, modal } = sagaPayload as GetManualCommunicationTemplatesRequestPayload;
    const errorMessage = 'Failed to fetch manual communication template options!';

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_MANUAL_COMMUNICATION_TEMPLATE_OPTIONS
            }
        );

        const { Templates } = get(res, 'data.GetManualCommunicationTemplateOptions');
        yield put(getManualCommunicationTemplatesActionSuccess(Templates));

        if (callback) {
            callback(Templates);
        }
    }
    catch (err) {
        yield put(getManualCommunicationTemplatesActionError([errorMessage]));

        const onCloseCallback = () => {
            if (callback) {
                callback([]);
            }
        };

        modal?.error({
            title: getTranslatedText("Error"),
            content: getTranslatedText(errorMessage),
            onOk: onCloseCallback,
            onCancel: onCloseCallback,
            okText: getTranslatedText("OK")
        });

        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Handler for uploading images from CKEditor
 */
function* handleUploadImageForEditor({ payload: sagaPayload }: any) {
    const { fileName, base64Data, contentType, companyId, callback } = sagaPayload;

    try {
        // Upload image with base64 data
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/content/image-upload',
            options: {
                body: {
                    Base64Data: base64Data,
                    CompanyId: companyId,
                    FileName: fileName,
                    ContentType: contentType,
                    FolderPath: 'content/images'
                }
            }
        });

        const responseBody = yield body.json();
        
        console.log('Image upload response:', responseBody);

        if (responseBody && responseBody.ImageUrl) {
            // Return S3 URL to callback
            if (callback) {
                callback({
                    IsSuccess: true,
                    ImageUrl: responseBody.ImageUrl
                });
            }
        } else {
            console.error('Invalid response - missing ImageUrl:', responseBody);
            throw new Error('Failed to upload image - missing ImageUrl in response');
        }
    } catch (err) {
        console.error('Image upload error:', err);
        let returnData: any;

        if ((err as any).response && (err as any).response.body) {
            returnData = JSON.parse((err as any).response.body);
        } else if (err instanceof Error) {
            returnData = { Messages: [err.message] };
        } else {
            returnData = { Messages: ['An unknown error occurred.'] };
        }

        returnData.IsSuccess = false;
        
        if (callback) {
            callback(returnData);
        }

        console.log('Error uploading image:', err);
    }
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetWorkflowContentConfiguration() {
    yield takeLatest(
        getWorkflowContentConfigurationAction.type,
        handleGetWorkflowContentConfiguration
    );
}

function* watchGetContentTemplateConfiguration() {
    yield takeLatest(
        getContentTemplateConfigurationAction.type,
        handleGetContentTemplateConfiguration
    );
}

function* watchGetContentTemplate() {
    yield takeLatest(
        getContentTemplateAction.type,
        handleGetContentTemplate
    );
}

function* watchGetContentTemplatePreview() {
    yield takeLatest(
        getContentTemplatePreviewAction.type,
        handleGetContentTemplatePreview
    );
}

function* watchSaveContentTemplate() {
    yield takeLatest(
        saveContentTemplateAction.type,
        handleSaveContentTemplate
    );
}

function* watchGetManualCommunicationTemplateOptions() {
    yield takeLatest(
        getManualCommunicationTemplatesAction.type,
        handleGetManualCommunicationTemplateOptions
    );
}

function* watchUploadImageForEditor() {
    yield takeLatest(
        uploadImageForEditorAction.type,
        handleUploadImageForEditor
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* contentsSaga() {
    yield all([
        fork(watchGetWorkflowContentConfiguration),
        fork(watchGetContentTemplateConfiguration),
        fork(watchGetContentTemplate),
        fork(watchGetContentTemplatePreview),
        fork(watchSaveContentTemplate),
        fork(watchGetManualCommunicationTemplateOptions),
        fork(watchUploadImageForEditor)
    ]);
}

export default contentsSaga;
